import React, { useContext, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useField } from 'react-final-form';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useSequencePayload from '@ubeya/shared/hooks/account/useSequencePayload';
import useFields from '@ubeya/shared/hooks/account/useFields';
import { FieldTypes } from '@ubeya/shared/hooks/account/useFields';
import { ReactComponent as BaseInfoIcon } from '@ubeya/shared-web/assets/explanation.svg';
import { ReactComponent as BasePayrollIcon } from '@ubeya/shared-web/assets/payroll-dollar.svg';
import { ReactComponent as BaseTasksIcon } from '@ubeya/shared-web/assets/tasks.svg';
import { ReactComponent as AttachmentIcon } from '../../assets/link.svg';
import { SmallBody, smallBodySemiBold } from '../Typography';
import useFormFields from '../../hooks/useFormFields';
import { FlexColumn, flexMiddle } from '../Flex';
import { ReactComponent as ProfileFieldIcon } from '../../assets/employee.svg';
import { ExplanationTooltip, Tooltip as BaseTooltip } from '..';
import ConfigContext from '../../contexts/ConfigContext';
import ProfileInputModal from './ProfileInputModal';
import PayrollInputModal from './PayrollInputModal';
import TimesheetInputModal from './TimesheetInputModal';

const Wrapper = styled(FlexColumn)`
  padding: ${({ $isCompliance }) => ($isCompliance ? '8px !important' : '10px 0')};
  overflow: auto;
  min-width: 165px;
  gap: ${({ $isCompliance }) => ($isCompliance ? '4px' : '0px')};
  margin: ${({ $isCompliance }) => ($isCompliance ? '0px 15px 42px 15px' : '0px 0px 0px 0px')};
`;

const Tooltip = styled(BaseTooltip)`
  .rc-tooltip-inner {
    max-width: 200px;
    text-align: center;
  }
`;

const InfoIcon = styled(BaseInfoIcon)`
  width: 15px;
  height: 15px;
  margin: 0 0 2px 5px;
`;

const PayrollIcon = styled(BasePayrollIcon)`
  width: 15px;
  height: 15px;
`;

const TasksIcon = styled(BaseTasksIcon)`
  width: 15px;
  height: 15px;
`;

const Title = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  padding-bottom: 6px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary};
  margin: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${smallBodySemiBold}
  text-align: center;
`;

const FieldType = styled(SmallBody)`
  ${flexMiddle};
  color: ${({ theme }) => theme.colors.grey4};
  cursor: pointer;
  min-height: 40px;
  padding: ${({ $isCompliance }) => ($isCompliance ? '0px 13px' : '5px 20px')};
  background: ${({ $isCompliance, theme }) => ($isCompliance ? theme.colors.grey1 : 'auto')};

  &:hover {
    background: ${({ $isCompliance, theme }) => ($isCompliance ? theme.colors.hoverBackground : theme.colors.white)};
  }
`;

const StyledIcon = styled.div.attrs(() => ({ dir: 'rtl' }))`
  min-width: 20px;
  margin-right: 16px;
`;

const FieldsTypes = ({
  kind,
  includeUnsupportedInputs = false,
  includeContent = false,
  includeProfileFields = false,
  includePayrollFields = false,
  includeTimesheetFields = false,
  includeShowNotSupportedFields = false,
  includeOnlyAdminSupported = false,
  className,
  onClick,
  fieldsName = 'fields',
  allowedFieldTypes,
  isCompliance = false
}) => {
  const {
    slug: { myContent, myContentTooltip }
  } = useSequencePayload({ kind });
  const { t } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { mappedFields } = useFields();
  const { fieldTypesOptions } = useFormFields(
    includeUnsupportedInputs,
    includeContent,
    includeShowNotSupportedFields,
    includeOnlyAdminSupported
  );

  const contentFields = useMemo(
    () =>
      fieldTypesOptions.filter(({ content, value }) => {
        if (typeof allowedFieldTypes !== 'undefined') {
          if (!allowedFieldTypes.includes(value)) {
            return false;
          }
        }
        return content;
      }),
    [allowedFieldTypes, fieldTypesOptions]
  );

  const inputFields = useMemo(
    () =>
      fieldTypesOptions.filter(({ content, value }) => {
        if (typeof allowedFieldTypes !== 'undefined') {
          if (!allowedFieldTypes.includes(value)) {
            return false;
          }
        }
        return !content;
      }),
    [allowedFieldTypes, fieldTypesOptions]
  );

  const [showProfileInput, setShowProfileInput] = useState(false);
  const [showPayrollInput, setShowPayrollInput] = useState(false);
  const [showTimesheetInput, setShowTimesheetInput] = useState(false);

  const {
    input: { value: fields, onChange }
  } = useField(fieldsName);

  const handleFieldsConfirm = useCallback(
    (newFields, isProfileField = false) => {
      // Get IDs of newly selected fields
      const existingIds = new Set(fields.map((field) => field.id));
      const newFieldIds = new Set(newFields.map((field) => field.id));

      // Keep only fields that are either not the specific field type (id <= 0 - when editing, or no mappedFields record for profile) or are in the new selection
      const filteredFields = fields.filter((field) => {
        if (field.id <= 0) return true; // Keep new/unsaved fields
        if (isProfileField) {
          return !mappedFields?.[field.id] || newFieldIds.has(field.id);
        }
        return newFieldIds.has(field.id);
      });

      // Filter out any new fields that already exist
      const uniqueNewFields = newFields.filter((field) => !existingIds.has(field.id));

      onChange([...filteredFields, ...uniqueNewFields]);
    },
    [fields, mappedFields, onChange]
  );

  const handleProfileConfirm = useCallback((newFields) => handleFieldsConfirm(newFields, true), [handleFieldsConfirm]);

  return (
    <Wrapper className={className} $isCompliance={isCompliance}>
      {contentFields.length > 0 && (
        <Title>
          <ExplanationTooltip title={t(myContent)} body={t(myContentTooltip || 'tooltipAddworkflowcontent')} />
        </Title>
      )}

      {contentFields.map(({ value, label, icon: Icon }) => (
        <FieldType key={value} onClick={() => onClick(value)}>
          {Icon && <StyledIcon as={Icon} />}
          {label}
        </FieldType>
      ))}

      {inputFields.length > 0 && (
        <Title>
          <ExplanationTooltip title={t('inputField')} body={t('tooltipInputfield')} />
        </Title>
      )}

      {includeProfileFields && (
        <FieldType onClick={() => setShowProfileInput(true)}>
          <StyledIcon as={ProfileFieldIcon} />
          {t('profileField')}
          <Tooltip body={t('profileFieldTooltip')}>
            <InfoIcon />
          </Tooltip>
        </FieldType>
      )}

      {includePayrollFields && (
        <FieldType onClick={() => setShowPayrollInput(true)}>
          <StyledIcon as={PayrollIcon} />
          {t('payrollField')}
          <Tooltip body={t('payrollFieldTooltip')}>
            <InfoIcon />
          </Tooltip>
        </FieldType>
      )}

      {includeTimesheetFields && (
        <FieldType onClick={() => setShowTimesheetInput(true)}>
          <StyledIcon as={TasksIcon} />
          {t('timesheetField')}
          <Tooltip body={t('timesheetFieldTooltip')}>
            <InfoIcon />
          </Tooltip>
        </FieldType>
      )}

      {inputFields.map(({ value, label, icon: Icon, infoText }) => (
        <FieldType key={value} onClick={() => onClick(value)} $isCompliance={isCompliance}>
          {Icon && <StyledIcon as={isCompliance && value === FieldTypes.FIELD_TYPE_DOCUMENT ? AttachmentIcon : Icon} />}
          {label}
          {infoText && (
            <Tooltip body={infoText}>
              <InfoIcon />
            </Tooltip>
          )}
        </FieldType>
      ))}

      {showProfileInput && (
        <ProfileInputModal
          accountId={accountId}
          onClose={() => setShowProfileInput(false)}
          onConfirm={handleProfileConfirm}
          initialValues={(fields || [])
            .filter(({ id }) => id > 0 && !!mappedFields?.[id])
            .reduce((acc, { id }) => ({ ...acc, [`profile-field-${id}`]: true }), {})}
        />
      )}

      {showPayrollInput && (
        <PayrollInputModal
          accountId={accountId}
          onClose={() => setShowPayrollInput(false)}
          onConfirm={(newFields) => onChange([...fields, ...newFields])}
        />
      )}

      {showTimesheetInput && (
        <TimesheetInputModal
          accountId={accountId}
          onClose={() => setShowTimesheetInput(false)}
          onConfirm={(newFields) => handleFieldsConfirm(newFields, false)}
          initialValues={(fields || [])
            .filter(({ id }) => id > 0)
            .reduce((acc, { id }) => ({ ...acc, [`timesheet-field-${id}`]: true }), {})}
        />
      )}
    </Wrapper>
  );
};

export default FieldsTypes;
